import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Plus, RefreshCcw, X } from "lucide-react";
import { DefineAiDisclosure } from "./DefineAiDisclosure";

function normalizePreviewItems(items = []) {
  const filled = items.filter((item) => String(item || "").trim());
  return filled.length ? filled : ["No entries yet"];
}

function modeLabel(mode) {
  if (mode === "merge") return "Merge Items";
  if (mode === "keep") return "Keep Existing";
  return "Replace Section";
}

function SipocDraftSection({
  category,
  currentItems,
  draftItems,
  isSelected,
  mode,
  setSelected,
  setMode,
  updatePendingSipocItem,
  addPendingSipocItem,
  removePendingSipocItem,
  applySuggestionSection,
  skipSection,
}) {
  const hasCurrentContent = currentItems.some((item) => String(item || "").trim());

  return (
    <section className="rounded-2xl border border-border bg-background/40 p-5 shadow-sm" data-testid={`sipoc-review-section-${category.key}`}>
      <div className="flex flex-col gap-4 border-b border-border/70 pb-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelected(category.key, !isSelected)}
              className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                isSelected
                  ? "border-sky-500/30 bg-sky-500/10 text-sky-300"
                  : "border-border bg-card text-muted-foreground"
              }`}
              data-testid={`sipoc-review-select-${category.key}`}
            >
              {isSelected ? "Selected" : "Skipped"}
            </button>
            <p className="text-sm font-semibold text-foreground">{category.label}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Review the AI draft, edit it if needed, and choose how this section should affect your current SIPOC.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {["keep", "replace", "merge"].map((nextMode) => (
            <Button
              key={nextMode}
              type="button"
              variant={mode === nextMode ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setMode(category.key, nextMode);
                setSelected(category.key, nextMode !== "keep");
              }}
              data-testid={`sipoc-review-mode-${category.key}-${nextMode}`}
            >
              {modeLabel(nextMode)}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[0.95fr,1.35fr]">
        <div className="rounded-xl border border-border/80 bg-card/70 p-4" data-testid={`sipoc-review-current-${category.key}`}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Current SIPOC</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {hasCurrentContent
              ? "This is the content currently in your working SIPOC."
              : "No current content in this section yet."}
          </p>
          <div className="mt-3 space-y-2">
            {normalizePreviewItems(currentItems).map((item, index) => (
              <div key={`${category.key}-current-${index}`} className="rounded-lg border border-border/70 bg-background px-3 py-2 text-sm text-foreground/90">
                {item}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-sky-500/20 bg-sky-500/5 p-4" data-testid={`sipoc-review-draft-${category.key}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-300">AI Draft</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Draft items remain editable before you apply anything.
              </p>
            </div>
            <div className="rounded-full border border-border bg-card px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {modeLabel(mode)}
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {draftItems.map((item, index) => (
              <div key={`${category.key}-draft-${index}`} className="flex items-center gap-2">
                <Input
                  value={item}
                  onChange={(event) => updatePendingSipocItem(category.key, index, event.target.value)}
                  placeholder={category.placeholder}
                  className="bg-background/80"
                  data-testid={`sipoc-preview-${category.key}-${index}`}
                />
                {draftItems.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removePendingSipocItem(category.key, index)}
                    data-testid={`remove-sipoc-preview-${category.key}-${index}`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => addPendingSipocItem(category.key)}
              data-testid={`add-sipoc-preview-${category.key}-btn`}
            >
              <Plus className="mr-1 h-3 w-3" /> Add Draft Item
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applySuggestionSection(category.key)}
              disabled={mode === "keep"}
              data-testid={`apply-sipoc-section-${category.key}-btn`}
            >
              Apply Section
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => skipSection(category.key)}
              data-testid={`skip-sipoc-section-${category.key}-btn`}
            >
              Skip Section
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SipocDraftReviewModal({
  open,
  setOpen,
  pendingSuggestion,
  currentSipoc,
  selectedSections,
  sectionModes,
  setSectionSelected,
  setSectionMode,
  confirmApplySuggestion,
  applySuggestionSection,
  applySelectedSuggestionSections,
  updatePendingSipocItem,
  addPendingSipocItem,
  removePendingSipocItem,
  cancelSipocPreview,
  regenerateSipocSuggestion,
  aiGenerating,
}) {
  const categories = [
    { key: "suppliers", label: "Suppliers", placeholder: "Add supplier" },
    { key: "inputs", label: "Inputs", placeholder: "Add input" },
    { key: "process_steps", label: "Process", placeholder: "Add process step" },
    { key: "outputs", label: "Outputs", placeholder: "Add output" },
    { key: "customers", label: "Customers", placeholder: "Add customer" },
  ];

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        cancelSipocPreview();
      }
      setOpen(nextOpen);
    }}>
      <DialogContent
        className="w-[min(96vw,1280px)] max-w-6xl overflow-hidden border-border bg-card p-0 sm:rounded-2xl"
        data-testid="sipoc-preview-modal"
      >
        <DialogHeader className="border-b border-border/80 px-6 py-5 text-left">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="space-y-2">
              <div className="inline-flex items-center rounded-full border border-sky-500/25 bg-sky-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-sky-300">
                AI-Generated SIPOC Draft
              </div>
              <DialogTitle className="text-xl font-heading">Review SIPOC Draft Before Applying</DialogTitle>
              <DialogDescription className="max-w-2xl text-sm text-muted-foreground">
                Review and optionally apply this draft. Nothing will overwrite your current SIPOC until you confirm.
              </DialogDescription>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={regenerateSipocSuggestion}
                disabled={aiGenerating === "SIPOC Draft"}
                data-testid="regenerate-sipoc-preview-btn"
              >
                <RefreshCcw className="mr-2 h-4 w-4" /> Regenerate
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={applySelectedSuggestionSections}
                data-testid="apply-selected-sipoc-btn"
              >
                Apply Selected Sections
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={confirmApplySuggestion}
                data-testid="apply-all-sipoc-btn"
              >
                Apply All
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[78vh] overflow-y-auto px-6 py-5">
          <div className="space-y-5">
            <DefineAiDisclosure
              title="Draft review only"
              detail="Current SIPOC content stays intact until you explicitly apply all sections or selected sections. Use Keep Existing, Replace Section, or Merge Items per section."
              testId="sipoc-draft-review-disclosure"
            />

            {pendingSuggestion && categories.map((category) => (
              <SipocDraftSection
                key={category.key}
                category={category}
                currentItems={currentSipoc?.[category.key] || []}
                draftItems={pendingSuggestion?.[category.key] || [""]}
                isSelected={Boolean(selectedSections?.[category.key])}
                mode={sectionModes?.[category.key] || "replace"}
                setSelected={setSectionSelected}
                setMode={setSectionMode}
                updatePendingSipocItem={updatePendingSipocItem}
                addPendingSipocItem={addPendingSipocItem}
                removePendingSipocItem={removePendingSipocItem}
                applySuggestionSection={applySuggestionSection}
                skipSection={(section) => {
                  setSectionMode(section, "keep");
                  setSectionSelected(section, false);
                }}
              />
            ))}
          </div>
        </div>

        <DialogFooter className="border-t border-border/80 px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={cancelSipocPreview}
            data-testid="cancel-sipoc-preview-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={applySelectedSuggestionSections}
            data-testid="apply-selected-sipoc-footer-btn"
          >
            Apply Selected Sections
          </Button>
          <Button type="button" onClick={confirmApplySuggestion} data-testid="apply-all-sipoc-footer-btn">
            Apply All
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
